/**
 * PATH: apps/qa/src/pages/reports/GenerateReportDialog.tsx
 * PURPOSE: Modal dialog for generating QA reports from inspections
 * WHY: Extracted from ReportsPage.tsx to keep files under 300 lines
 */

import { useEffect, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { reportApi, inspectionApi } from '@/lib/api'
import {
  X,
  Loader2,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { STANDARD_REPORTS } from './report-helpers'

interface GenerateDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialType?: string
}

export function GenerateReportDialog({ isOpen, onClose, onSuccess, initialType = 'qa_pack' }: GenerateDialogProps) {
  const [selectedInspection, setSelectedInspection] = useState<string>('')
  const [reportType, setReportType] = useState<string>(initialType)
  const [reportName, setReportName] = useState('')

  useEffect(() => {
    if (isOpen) setReportType(initialType)
  }, [isOpen, initialType])
  
  const { data: inspectionsData, isLoading: inspectionsLoading } = useQuery({
    queryKey: ['qa-inspections-for-report'],
    queryFn: () => inspectionApi.getInspections({ page_size: 100 }),
    enabled: isOpen,
  })
  
  const inspections = inspectionsData?.results || []
  
  const generateMutation = useMutation({
    mutationFn: async () => {
      return reportApi.generateReport({
        inspection_id: selectedInspection,
        report_type: reportType,
        report_name: reportName || undefined,
      })
    },
    onSuccess: () => {
      onSuccess()
      onClose()
      setSelectedInspection('')
      setReportName('')
    },
  })
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Generate Report</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Inspection selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Inspection <span className="text-destructive">*</span>
            </label>
            {inspectionsLoading ? (
              <div className="py-3 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading inspections...
              </div>
            ) : (
              <select
                value={selectedInspection}
                onChange={(e) => setSelectedInspection(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Select an inspection</option>
                {inspections.map((insp) => (
                  <option key={insp.id} value={insp.id}>
                    {insp.inspection_name} ({insp.status})
                  </option>
                ))}
              </select>
            )}
          </div>
          
          {/* Report type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Report Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {STANDARD_REPORTS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setReportType(value)}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-colors',
                    reportType === value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:bg-accent'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Custom name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Report Name (optional)
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Auto-generated if left empty"
              className="w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => generateMutation.mutate()}
            disabled={!selectedInspection || generateMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Generate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
